/**
 * AnnotationSpeedDashboard — Per-project annotation throughput and timing dashboard.
 *
 * Displays:
 * - Total annotation count with category and annotator breakdowns
 * - Timing statistics (mean, median, min, max duration per annotation)
 * - Per-annotator speed comparison
 * - Recent activity feed
 *
 * Data comes from GET /api/labeling/projects/{project_id}/annotation-stats
 * which returns counts + recent_activity (full annotation records with timing).
 */

import { useMemo, useState } from "react";
import {
  useAnnotationStatsQuery,
  useAnnotatorComparisonQuery,
} from "../../hooks/useLabelingQueries";

interface AnnotationSpeedDashboardProps {
  projectId: string;
}

interface TimingStats {
  count: number;
  mean: number;
  median: number;
  min: number;
  max: number;
  std: number;
}

interface AnnotatorTiming {
  annotator: string;
  count: number;
  mean: number;
  median: number;
}

function computeTimingStats(durations: number[]): TimingStats | null {
  if (durations.length === 0) return null;
  const sorted = [...durations].sort((a, b) => a - b);
  const mean = sorted.reduce((a, b) => a + b, 0) / sorted.length;
  const median =
    sorted.length % 2 === 0
      ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
      : sorted[Math.floor(sorted.length / 2)];
  const variance = sorted.reduce((sum, v) => sum + (v - mean) ** 2, 0) / sorted.length;
  return {
    count: sorted.length,
    mean,
    median,
    min: sorted[0],
    max: sorted[sorted.length - 1],
    std: Math.sqrt(variance),
  };
}

function extractDuration(activity: Record<string, any>): number | null {
  const timing = activity.timing;
  if (!timing) return null;
  let parsed: Record<string, any>;
  try {
    parsed = typeof timing === "string" ? JSON.parse(timing) : timing;
  } catch {
    return null;
  }
  const dur = parsed.duration_seconds;
  if (dur == null || dur <= 0) return null;
  if (typeof dur === "string") return parseInt(dur, 10) || null;
  return dur;
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function formatTimeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function StatCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="bg-surface rounded-lg p-3">
      <div className="text-xs text-fg-muted uppercase tracking-wide">{label}</div>
      <div className="text-xl font-semibold text-fg mt-1">{value}</div>
      {sub && <div className="text-xs text-fg-muted mt-0.5">{sub}</div>}
    </div>
  );
}

export function AnnotationSpeedDashboard({ projectId }: AnnotationSpeedDashboardProps) {
  const { data: stats, isLoading, error } = useAnnotationStatsQuery(projectId);

  const { timingStats, perAnnotator, perCategory } = useMemo(() => {
    if (!stats) return { timingStats: null, perAnnotator: [], perCategory: [] };

    // Use server-computed timing stats (no client-side fallback)
    const timingStats: TimingStats | null = stats.timing_stats ?? null;

    // Use server-computed per-annotator timing
    let perAnnotator: AnnotatorTiming[] = [];
    if (stats.per_annotator_timing) {
      perAnnotator = Object.entries(stats.per_annotator_timing)
        .map(([annotator, t]) => ({ annotator, count: t.count, mean: t.mean, median: t.median }))
        .sort((a, b) => b.count - a.count);
    }

    // Per-category: always from recent_activity (no server-side equivalent yet)
    const perCategory: { category: string; count: number; mean: number; median: number }[] = [];
    if (stats.recent_activity) {
      const byCategory = new Map<string, number[]>();
      for (const a of stats.recent_activity) {
        const dur = extractDuration(a);
        if (dur != null) {
          const cat = a.category ?? "unknown";
          if (!byCategory.has(cat)) byCategory.set(cat, []);
          byCategory.get(cat)!.push(dur);
        }
      }
      for (const [category, durations] of Array.from(byCategory.entries())) {
        const s = computeTimingStats(durations)!;
        perCategory.push({ category, count: s.count, mean: s.mean, median: s.median });
      }
      perCategory.sort((a, b) => b.count - a.count);
    }

    return { timingStats, perAnnotator, perCategory };
  }, [stats]);

  // Inter-annotator comparison: auto-select annotator with most annotations as ground truth
  const annotatorEmails = useMemo(() => {
    if (!stats?.annotations_by_annotator) return [];
    return Object.entries(stats.annotations_by_annotator)
      .sort(([, a], [, b]) => b - a)
      .map(([email]) => email);
  }, [stats]);

  const [groundTruth, setGroundTruth] = useState<string>("");
  const effectiveGroundTruth = groundTruth || annotatorEmails[0] || "";
  const {
    data: comparison,
    isLoading: compLoading,
    error: compError,
  } = useAnnotatorComparisonQuery(projectId, effectiveGroundTruth);

  if (isLoading) {
    return <div className="p-4 text-fg-muted text-sm">Loading annotation stats...</div>;
  }

  if (error) {
    return (
      <div className="p-4 text-warn text-sm">Failed to load stats: {(error as Error).message}</div>
    );
  }

  if (!stats) return null;

  return (
    <div className="p-4 space-y-4 overflow-y-auto h-full">
      <h2 className="text-lg font-semibold text-fg">Annotation Dashboard</h2>
      <p className="text-xs text-fg-muted">{projectId}</p>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-2">
        <StatCard label="Total" value={stats.total_annotations} />
        <StatCard
          label="Categories"
          value={Object.keys(stats.annotations_by_category).length}
          sub={Object.entries(stats.annotations_by_category)
            .map(([k, v]) => `${k}: ${v}`)
            .join(", ")}
        />
        <StatCard label="Annotators" value={Object.keys(stats.annotations_by_annotator).length} />
        {timingStats && (
          <StatCard
            label="Avg Time"
            value={formatDuration(timingStats.mean)}
            sub={`median ${formatDuration(timingStats.median)}`}
          />
        )}
      </div>

      {/* Timing detail */}
      {timingStats ? (
        <div className="bg-surface rounded-lg p-3">
          <div className="text-xs text-fg-muted uppercase tracking-wide mb-2">
            Timing ({timingStats.count} timed)
          </div>
          <div className="grid grid-cols-4 gap-2 text-center text-sm">
            <div>
              <div className="text-fg-muted text-xs">Min</div>
              <div className="text-fg">{formatDuration(timingStats.min)}</div>
            </div>
            <div>
              <div className="text-fg-muted text-xs">Mean</div>
              <div className="text-fg">{formatDuration(timingStats.mean)}</div>
            </div>
            <div>
              <div className="text-fg-muted text-xs">Median</div>
              <div className="text-fg">{formatDuration(timingStats.median)}</div>
            </div>
            <div>
              <div className="text-fg-muted text-xs">Max</div>
              <div className="text-fg">{formatDuration(timingStats.max)}</div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-surface rounded-lg p-3">
          <div className="text-xs text-fg-muted uppercase tracking-wide mb-1">Timing</div>
          <div className="text-xs text-fg-muted">No timing data available</div>
        </div>
      )}

      {/* Per-annotator */}
      {perAnnotator.length > 0 && (
        <div className="bg-surface rounded-lg p-3">
          <div className="text-xs text-fg-muted uppercase tracking-wide mb-2">By Annotator</div>
          <div className="space-y-2">
            {perAnnotator.map((a) => (
              <div key={a.annotator} className="flex justify-between items-center text-sm">
                <span className="text-fg-muted truncate max-w-[60%]">{a.annotator}</span>
                <span className="text-fg">
                  {a.count} ann &middot; {formatDuration(a.mean)} avg
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per-category */}
      {perCategory.length > 0 && (
        <div className="bg-surface rounded-lg p-3">
          <div className="text-xs text-fg-muted uppercase tracking-wide mb-2">By Category</div>
          <div className="space-y-2">
            {perCategory.map((c) => (
              <div key={c.category} className="flex justify-between items-center text-sm">
                <span className="text-fg-muted">{c.category}</span>
                <span className="text-fg">
                  {c.count} ann &middot; {formatDuration(c.mean)} avg
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Inter-annotator agreement */}
      {annotatorEmails.length >= 2 && (
        <div className="bg-surface rounded-lg p-3">
          <div className="text-xs text-fg-muted uppercase tracking-wide mb-2">
            Inter-Annotator Agreement
          </div>
          <div className="mb-2">
            <select
              value={effectiveGroundTruth}
              onChange={(e) => setGroundTruth(e.target.value)}
              className="w-full bg-surface-2 text-fg text-xs rounded px-2 py-1 border border-border"
            >
              {annotatorEmails.map((email) => (
                <option key={email} value={email}>
                  {email}
                </option>
              ))}
            </select>
            <div className="text-[10px] text-fg-muted mt-0.5">Ground truth annotator</div>
          </div>
          {compLoading && <div className="text-xs text-fg-muted">Loading comparison...</div>}
          {compError && <div className="text-xs text-fg-muted">Comparison not available</div>}
          {comparison && comparison.candidates.length > 0 && (
            <div className="space-y-2">
              {comparison.candidates.map((c) => (
                <div key={c.annotator} className="border border-border-strong rounded p-2">
                  <div className="flex justify-between items-center text-sm mb-1">
                    <span className="text-fg-muted truncate max-w-[60%]">{c.annotator}</span>
                    <span className="text-fg font-medium">
                      {(c.overall_agreement * 100).toFixed(1)}%
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-1 text-[10px] text-fg-muted">
                    <div>Matched: {c.matched_pairs}</div>
                    <div>Studies: {c.studies_in_common}</div>
                    <div>
                      {c.gt_timing_median > 0 && c.candidate_timing_median > 0
                        ? `${formatDuration(c.gt_timing_median)} vs ${formatDuration(c.candidate_timing_median)}`
                        : ""}
                    </div>
                  </div>
                  {Object.keys(c.field_agreements).length > 0 && (
                    <div className="mt-1 space-y-0.5">
                      {Object.entries(c.field_agreements).map(([field, rate]) => (
                        <div key={field} className="flex justify-between text-[10px]">
                          <span className="text-fg-muted">{field}</span>
                          <span className="text-fg-muted">{(rate * 100).toFixed(0)}%</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {comparison && comparison.candidates.length === 0 && (
            <div className="text-xs text-fg-muted">No other annotators to compare</div>
          )}
        </div>
      )}

      {/* Recent activity */}
      {(stats.recent_activity?.length ?? 0) > 0 && (
        <div className="bg-surface rounded-lg p-3">
          <div className="text-xs text-fg-muted uppercase tracking-wide mb-2">Recent Activity</div>
          <div className="space-y-1.5">
            {stats.recent_activity.slice(0, 10).map((a, i) => {
              const dur = extractDuration(a);
              return (
                <div key={i} className="flex justify-between text-xs">
                  <span className="text-fg-muted truncate max-w-[50%]">
                    {a.category ?? "unknown"}
                  </span>
                  <span className="text-fg-muted">
                    {dur != null ? formatDuration(dur) : "—"}
                    {a.annotated_at ? ` · ${formatTimeAgo(a.annotated_at)}` : ""}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
